import { type ReactNode } from 'react'
import { BackLink } from '#app/components/back-link.tsx'
import { PageFrame, ReadingColumn } from '#app/components/page-frame.tsx'
import { type ArticleMeta } from './articles.ts'
import { ProblemCapsule } from './components/problem-capsule.tsx'
import { formatDate } from './format-date.ts'

// Wraps a post's MDX body with its title, date, a back-link to the index, the
// problem/approach capsule, and Tailwind `prose` typography. Ported from na-bjj's
// ArticleLayout; each content/writing/*.mdx default-exports
// `(props) => <ArticleLayout .../>`.
//
// The capsule renders from `article` metadata rather than being placed by hand in
// each MDX body (it was, until 2026-08-04). Rendering it here is what makes it
// structural: a post with no capsule copy fails articles.test.ts instead of
// silently publishing without the one element every post is supposed to share.
export function ArticleLayout({
	article,
	children,
}: {
	article: ArticleMeta
	children: ReactNode
}) {
	return (
		<PageFrame as="div">
			<ReadingColumn>
				<BackLink fallback={{ to: '/writing', label: 'Writing' }} />
				<article>
					<header className="flex flex-col">
						<time
							dateTime={article.date}
							className="text-muted-foreground order-first text-sm"
						>
							{formatDate(article.date)}
						</time>
						<h1 className="mt-2 text-3xl font-bold tracking-tight text-pretty sm:text-4xl">
							{article.title}
						</h1>
					</header>
					<ProblemCapsule
						problem={article.hardProblem}
						approach={article.approach}
					/>
					<div className="prose dark:prose-invert max-w-none">{children}</div>
				</article>
			</ReadingColumn>
		</PageFrame>
	)
}
